import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import cookiesConstants from '@/constants/cookies.constants';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import handleLanguageChange from '@/utilites/handle-language-change';
import useBackendUser from '@/hooks/useBackendUser';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import parseWithType from '@/utilites/parse-with-type';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { Locales } from '@/types/enums';
import { PrimaryButton } from '../common/PrimaryButton';

export default function LanguageFloatingIcon() {
  /* ───────────────────────────────────── */
  /* State & helpers                       */
  /* ───────────────────────────────────── */
  const { data: user, isLoading } = useBackendUser();
  const queryClient = useQueryClient();
  const { t: tCommon } = useTranslation('common');
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [suggestedLocale, setSuggestedLocale] = useState<Locales | null>(null);
  const { translate } = useTypedTranslation<CommonEnum>('common', { lng: suggestedLocale ?? 'en' });
  const { locale = 'en' } = router;

  /* ───────────────────────────────────── */
  /* Emoji flags                           */
  /* ───────────────────────────────────── */
  const flagEmojis: Record<Locales, string> = {
    en: '🇬🇧',
    fr: '🇫🇷',
    de: '🇩🇪',
    es: '🇪🇸',
    it: '🇮🇹',
  } as const;

  /* ───────────────────────────────────── */
  /* Effects                               */
  /* ───────────────────────────────────── */
  useEffect(() => setIsMounted(true), []);

  useEffect(() => {
    if (!user && !isLoading) {
      const cookie = getCookie(cookiesConstants.COOKIES.LANGUAGE_MODAL.name);
      const dismissed = getCookie(cookiesConstants.COOKIES.LANGUAGE_MODAL_DISMISSED.name);
      if (dismissed) return setShowBanner(false);

      const cookieData = cookie ? parseWithType<{ show: boolean; locale: string }>(cookie) : null;
      if (cookieData?.show && cookieData?.locale) {
        setShowBanner(true);
        const localeTyped = Object.values(Locales).find(
          l => l.toLowerCase() === cookieData.locale.toLowerCase()
        );
        setSuggestedLocale(localeTyped || null);
      }
    }
  }, [user, locale, isLoading]);

  /* ───────────────────────────────────── */
  /* Helpers                               */
  /* ───────────────────────────────────── */
  const getLanguageMessage = () => {
    if (!suggestedLocale) return '';
    const wouldYouLike = translate(
      CommonEnum['would_you_like_to_switch_the_site_language_to_languageKey']
    );
    const suggestedLocaleLabel = translate(
      CommonEnum[`languages.${suggestedLocale}` as keyof typeof CommonEnum]
    );
    return wouldYouLike.replace('{{languageKey}}', suggestedLocaleLabel);
  };

  const handleSelectLanguage = async (localeKey: string) => {
    await handleLanguageChange(localeKey, tCommon, router, !!user, queryClient);
    setShowBanner(false);
    deleteCookie(cookiesConstants.COOKIES.LANGUAGE_MODAL.name);
  };

  const handleNoThanks = () => {
    deleteCookie(cookiesConstants.COOKIES.LANGUAGE_MODAL.name);
    setCookie(cookiesConstants.COOKIES.LANGUAGE_MODAL_DISMISSED.name, '1', {
      maxAge: 60 * 60 * 24 * 365,
      path: '/',
    });
    setShowBanner(false);
  };

  /* ───────────────────────────────────── */
  /* Early exits                           */
  /* ───────────────────────────────────── */
  if (!isMounted || !showBanner || !suggestedLocale) return null;

  /* ───────────────────────────────────── */
  /* Render                                */
  /* ───────────────────────────────────── */
  return (
    <div className="animate-slide-up tw-fixed tw-inset-x-0 tw-bottom-2 tw-z-50 tw-flex tw-justify-center tw-px-2 sm:tw-px-4 md:tw-bottom-4 lg:tw-bottom-6 lg:tw-px-6">
      <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-3 tw-rounded-lg tw-bg-gradient-to-r tw-from-main tw-to-main-shades-500 tw-px-4 tw-py-3 tw-text-white tw-shadow-2xl tw-ring-1 tw-ring-main-shades-200 sm:tw-max-w-md sm:tw-flex-row sm:tw-gap-4 sm:tw-rounded-xl sm:tw-px-6 sm:tw-py-4 md:tw-max-w-xl lg:tw-max-w-2xl lg:tw-gap-6 lg:tw-px-8 lg:tw-py-5">
        {/* Flag */}
        <span className="tw-text-2xl lg:tw-text-3xl" role="img" aria-label={suggestedLocale}>
          {flagEmojis[suggestedLocale] ?? '🌐'}
        </span>

        {/* Message */}
        <span className="tw-flex-1 tw-text-center tw-text-sm tw-font-medium sm:tw-text-left sm:tw-text-base lg:tw-text-lg">
          {getLanguageMessage()}
        </span>

        {/* Buttons */}
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-2 sm:tw-w-auto sm:tw-flex-row">
          <PrimaryButton
            onClick={() => handleSelectLanguage(suggestedLocale)}
            className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1 tw-rounded-md tw-bg-white tw-px-4 tw-py-2 tw-font-semibold tw-text-main hover:tw-bg-main-shades-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-main sm:tw-w-auto"
          >
            <span>🚀</span>
            <span>Yes</span>
          </PrimaryButton>

          <PrimaryButton
            onClick={handleNoThanks}
            className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1 tw-rounded-md tw-border tw-border-white tw-bg-transparent tw-px-4 tw-py-2 tw-font-semibold hover:tw-bg-main-shades-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-main sm:tw-w-auto"
          >
            <span>🙈</span>
            <span>No</span>
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
